import { useQuery } from '@tanstack/react-query';
import { broadcasts, channelWa, contacts, templates } from '@repletoryreach/sdk-api';
import type { ChecklistItem, Guide } from '../types';
import { builders } from './helpers';

// The Audience guide (8 October 2026): Contacts, Compose and Broadcasts. Words under guide.audience.*.

const { link, howLink, step, how, section, howTo } = builders('audience');
const L = {
  contacts: link('contacts', '/contacts'),
  compose: link('compose', '/inbox/compose'),
  broadcasts: link('broadcasts', '/broadcasts'),
  newBroadcast: link('newBroadcast', '/broadcasts/new'),
  delivery: link('delivery', '/broadcasts/delivery'),
  templates: link('templates', '/templates'),
  numbers: link('numbers', '/numbers'),
  inbox: link('inbox', '/inbox'),
  fields: link('fields', '/settings/contact-fields'),
  billing: link('billing', '/billing'),
  howContact: howLink('howContact', 'contact'),
  howImport: howLink('howImport', 'import'),
  howCompose: howLink('howCompose', 'compose'),
  howBroadcast: howLink('howBroadcast', 'broadcast')
};

function useAudienceChecklist(): { items: ChecklistItem[]; loading: boolean } {
  const numbers = useQuery({ queryKey: ['numbers'], queryFn: () => channelWa.listPhoneNumbers(), staleTime: 60_000 });
  const people = useQuery({ queryKey: ['contacts', 'count'], queryFn: () => contacts.listContacts({ limit: 1 }), staleTime: 60_000 });
  const tags = useQuery({ queryKey: ['contact-tags', 'guide'], queryFn: () => contacts.listTags(), staleTime: 60_000 });
  const tpl = useQuery({ queryKey: ['templates'], queryFn: () => templates.list(), staleTime: 60_000 });
  const sent = useQuery({ queryKey: ['broadcasts', 'guide'], queryFn: () => broadcasts.listBroadcasts({ limit: 1 }), staleTime: 60_000 });
  const item = (id: string, to: string, done: boolean | undefined, optional = false): ChecklistItem => ({
    id, to, done, optional, titleKey: `guide.audience.check.${id}.title`, hintKey: `guide.audience.check.${id}.hint`
  });
  const items = [
    item('number', '/numbers', numbers.data ? (numbers.data.data?.length ?? 0) > 0 : undefined),
    item('contacts', '/contacts', people.data ? (people.data.meta?.total ?? people.data.data?.length ?? 0) > 0 : undefined),
    item('consent', '/contacts', undefined),
    item('tags', '/contacts', tags.data ? (tags.data.data?.length ?? 0) > 0 : undefined),
    item('template', '/templates', tpl.data ? (tpl.data.data ?? []).some((x) => x.status === 'approved') : undefined),
    item('broadcast', '/broadcasts/new', sent.data ? (sent.data.data?.length ?? 0) > 0 : undefined, true)
  ];
  return { items, loading: [numbers, people, tags, tpl, sent].some((q) => q.isPending) };
}

export const AUDIENCE_GUIDE: Guide = {
  id: 'audience',
  version: 1,
  titleKey: 'guide.audience.title',
  introKey: 'guide.audience.intro',
  routes: ['/contacts', '/inbox/compose', '/broadcasts'],
  tour: [
    step('welcome', undefined, undefined, [L.numbers]),
    step('list', '/contacts', 'contacts-list'),
    step('filters', '/contacts', 'contacts-filters'),
    step('new', '/contacts', 'contacts-new', [L.howContact]),
    step('import', '/contacts', 'contacts-import', [L.howImport]),
    step('erase', '/contacts', 'contacts-erase'),
    step('composeWho', '/inbox/compose', 'compose-who', [L.howCompose]),
    step('composeWhat', '/inbox/compose', 'compose-what', [L.templates]),
    step('composeSend', '/inbox/compose', 'compose-send'),
    step('broadcasts', '/broadcasts', 'broadcasts-list'),
    step('newBroadcast', '/broadcasts', 'broadcasts-new', [L.howBroadcast]),
    step('delivery', '/broadcasts', 'broadcasts-delivery'),
    step('help', '/broadcasts', 'audience-help')
  ],
  howTos: [
    howTo('contact', [
      how('contact', 'details', '/contacts', 'contact-details', { open: 'contacts-new' }),
      how('contact', 'consent', '/contacts', 'contact-details', { open: 'contacts-new' }),
      how('contact', 'save', '/contacts', 'contact-save', { open: 'contacts-new', links: [L.fields] })
    ]),
    howTo('import', [
      how('import', 'button', '/contacts', 'contacts-import'),
      how('import', 'after', '/contacts', undefined, { links: [L.fields] })
    ]),
    howTo('compose', [
      how('compose', 'who', '/inbox/compose', 'compose-who'),
      how('compose', 'what', '/inbox/compose', 'compose-what', { links: [L.templates] }),
      how('compose', 'send', '/inbox/compose', 'compose-send', { links: [L.inbox] })
    ]),
    howTo('broadcast', [
      how('broadcast', 'steps', '/broadcasts/new', 'broadcast-steps'),
      how('broadcast', 'audience', '/broadcasts/new', 'broadcast-wizard'),
      how('broadcast', 'message', '/broadcasts/new', 'broadcast-wizard', { links: [L.templates] }),
      how('broadcast', 'when', '/broadcasts/new', 'broadcast-wizard'),
      how('broadcast', 'review', '/broadcasts/new', 'broadcast-wizard', { links: [L.billing, L.delivery] })
    ])
  ],
  sections: [
    section('overview', 3, 1, [L.contacts, L.compose, L.broadcasts]),
    section('contacts', 3, 2, [L.howContact, L.fields]),
    section('import', 2, 1, [L.howImport]),
    section('consent', 3, 2, [L.contacts]),
    section('tags', 2, 1, [L.contacts, L.newBroadcast]),
    section('window', 2, 1, [L.templates]),
    section('compose', 2, 1, [L.howCompose, L.inbox]),
    section('broadcasts', 3, 2, [L.howBroadcast, L.delivery]),
    section('costs', 2, 1, [L.billing]),
    section('privacy', 2, 1, [L.contacts]),
    section('trouble', 4, 0, [L.templates, L.numbers, L.billing])
  ],
  useChecklist: useAudienceChecklist
};
